/**
 * Form-level failure: the message that belongs to the whole submission rather
 * than to one field (bad credentials, rate limited, a conflict).
 *
 * `role="alert"` is on the element itself, so it is announced the moment it is
 * inserted — a person using a screen reader learns the submit failed without
 * having to go looking for the reason.
 */
import { TriangleAlert } from 'lucide-react';

import { cn } from '@wend/ui';

export interface FormAlertProps {
  children: string;
  /** Request id from the error envelope, when there is one. */
  requestId?: string;
  className?: string;
}

export function FormAlert({ children, requestId, className }: FormAlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-base border border-bad/45 bg-bad/10 px-3.5 py-3',
        className,
      )}
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-bad" />
      <div className="min-w-0">
        <p className="text-[0.88rem] leading-snug text-text">{children}</p>
        {requestId !== undefined && (
          <p className="mt-1 font-mono text-[0.68rem] tracking-[0.06em] text-muted">
            Request {requestId}
          </p>
        )}
      </div>
    </div>
  );
}
